/**
 * PURE. Garb and props (creatures §B7.4; art §5.7): garments shrink-wrapped on the torso/limb lofts (+0.008 H, mail/plate
 * +0.012) with skirts on the cloth bones (or 50 % pelvis / 50 % thighs), capes as double-sided cloth grids on the cloth
 * chain (lining = dye 1), headwear (helmets with ear holes — the ears poke through; hoods with ear pockets), cosmetic
 * wings, footwear, belt and neck items, idle props (hidden in battle). Cloth uses the dye slots (materials 5–8), metal
 * trims follow the «Краски» metal choice.
 */
import type { BoneName } from '../../shared';
import { HAT_HEIGHT } from '../rig/skeleton';
import type { Surface } from './builder';
import { torsoDeform, torsoRingAt } from './body';
import { MAT, REG, part, type BuildCtx } from './ctx';
import { add, blend, box, cross, ellipse, extrudeProfile, fan, lerp3, loft, nrm, rigid, scl, strip, sub, type Ring, type Skin, type V3 } from './prims';

const TAU = Math.PI * 2;
const DEG = Math.PI / 180;
const METAL_MAT = [MAT.gold, MAT.silver, MAT.iron, MAT.copper];

/** An open sheet over a θ arc of ellipse rings (both faces: outer `matOut`, inner `matIn`). θ in turns (0.25 = front). */
function arcSheet(s: Surface, rows: Array<{ c: V3; rs: number; rf: number; skin: (th: number, p: V3) => Skin; lw?: number; deform?: (p: V3, th: number) => V3 }>,
  th0: number, th1: number, cols: number, matOut: number, matIn: number, region: number, axis: V3 = [0, 1, 0]): number[][] {
  const grid: number[][] = [];
  for (const r of rows) {
    const line: number[] = [];
    for (let k = 0; k <= cols; k++) {
      const th = th0 + ((th1 - th0) * k) / cols;
      const a = th * TAU;
      let p: V3 = [r.c[0] - Math.cos(a) * r.rs, r.c[1], r.c[2] + Math.sin(a) * r.rf];
      if (r.deform) p = r.deform(p, ((th % 1) + 1) % 1);
      line.push(s.vert({ p, u: 0, v: k / cols, ...r.skin(((th % 1) + 1) % 1, p), lw: r.lw ?? 1 }));
    }
    grid.push(line);
  }
  for (let i = 0; i + 1 < grid.length; i++) {
    for (let k = 0; k < cols; k++) {
      const a = grid[i][k], b = grid[i][k + 1], c = grid[i + 1][k + 1], d = grid[i + 1][k];
      // orient the outer face away from the row axis
      const pa = s.verts[a].p, pb = s.verts[b].p, pc = s.verts[c].p;
      const n = cross(sub(pb, pa), sub(pc, pa));
      const ctr: V3 = scl(add(add(pa, pb), pc), 1 / 3);
      const ax = rows[i].c;
      const radial = sub(ctr, add(ax, scl(axis, (ctr[1] - ax[1]) * axis[1])));
      const outward = n[0] * radial[0] + n[2] * radial[2] + n[1] * radial[1] * (1 - Math.abs(axis[1])) >= 0;
      const out = { mat: matOut, region }, inn = { mat: matIn, region, hull: false };
      if (outward) { s.tri(a, b, c, out); s.tri(a, c, d, out); s.tri(a, c, b, inn); s.tri(a, d, c, inn); }
      else { s.tri(a, c, b, out); s.tri(a, d, c, out); s.tri(a, b, c, inn); s.tri(a, c, d, inn); }
    }
  }
  return grid;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── garments ──
function garment(ctx: BuildCtx): void {
  const { spec, d } = ctx;
  const g = spec.o.garment;
  if (g === 'none') return;
  const metal = METAL_MAT[spec.f.metal] ?? MAT.gold;
  const heavy = g === 'mail' || g === 'plate' || g === 'pots';
  const off = heavy ? 0.013 : 0.009;
  const mat = g === 'mail' || g === 'plate' || g === 'pots' ? MAT.iron : g === 'alb' ? MAT.parchment : g === 'apron' ? MAT.leather : g === 'sack' ? MAT.parchment
    : g === 'tabard' || g === 'surcoat' ? MAT.tinctA : MAT.dye0;
  const pelvis = ctx.bi('pelvis');
  const pelvisY = ctx.P.pelvis![1];
  const long = ['robe', 'alb', 'chasuble', 'ermine', 'motley', 'execTunic', 'smock'].includes(g);
  const knee = ctx.P.calf_L![1];
  const hemY = g === 'robe' || g === 'alb' || g === 'ermine' ? Math.max(0.035, d.footLen * 0.4) : g === 'motley' || g === 'execTunic' || g === 'chasuble' || g === 'smock'
    ? knee - 0.02 : g === 'tabard' || g === 'surcoat' ? knee + 0.01 : pelvisY - 0.11 * d.kL;
  const slug = spec.flags.slugBody;
  const skirtBones = ctx.rig.chains.skirt;
  const thL = ctx.ob('thigh_L'), thR = ctx.ob('thigh_R');
  part(ctx, 'garb.garment', (s) => {
    // Bodice: the torso rings + offset, from the hips to the shoulders.
    const t1 = g === 'apron' ? 0.82 : 0.97;
    const rings: Ring[] = [];
    const nT = ctx.cfg.torsoSides >= 8 ? 5 : 3;
    for (let k = 0; k < nT; k++) {
      const t = 0.12 + (t1 - 0.12) * (k / (nT - 1));
      const R = torsoRingAt(ctx, t);
      rings.push({ c: [0, R.y, 0.004 * t], rs: R.r + off, rf: R.r * 0.8 + off, skin: R.skin, lw: 1, deform: (p, th) => torsoDeform(p, th, R.bellyAmt * 1.02, R.hump) });
    }
    if (g !== 'apron' && g !== 'tabard' && g !== 'surcoat' && g !== 'chasuble') {
      loft(s, rings, { sides: ctx.cfg.torsoSides, front: [0, 0, 1], mat: g === 'motley' ? (_seg, th) => (Math.cos(th * TAU) < 0 ? MAT.dye0 : MAT.dye1) : mat,
        region: REG.torso, tangent: () => [0, 1, 0], creases: [{ ring: 0 }], capEnd: 'none', capStart: 'none' });
    }
    // Skirt / hem: cone from the hips to the hem (cloth bones when allocated, else 50 % pelvis / 50 % thighs).
    const R0 = torsoRingAt(ctx, 0.12);
    const rowsN = Math.max(2, Math.min(4, ctx.cfg.clothRows));
    const skirtRows: Array<{ c: V3; rs: number; rf: number; skin: (th: number, p: V3) => Skin; lw?: number }> = [];
    for (let k = 0; k <= rowsN; k++) {
      const u = k / rowsN;
      const y = R0.y + (hemY - R0.y) * u;
      const flare = 1 + (long ? 0.38 : 0.18) * u + (g === 'robe' || g === 'alb' ? 0.12 * u * u : 0);
      const rr = (R0.r + off) * flare;
      skirtRows.push({
        c: [0, y, 0.01 * u], rs: rr, rf: rr * 0.86, lw: k === rowsN ? 1.2 : 1,
        skin: (th, p) => {
          if (slug || u < 0.15) return rigid(pelvis);
          if (skirtBones.length === 4) { const left = p[0] >= 0; return blend(pelvis, skirtBones[left ? (u > 0.6 ? 2 : 0) : (u > 0.6 ? 3 : 1)], Math.min(1, u * 1.4)); }
          const th2 = p[0] >= 0 ? thL : thR;
          return th2 >= 0 ? blend(pelvis, th2, 0.5 * Math.min(1, u * 1.6)) : rigid(pelvis);
        },
      });
    }
    if (g === 'tabard' || g === 'surcoat' || g === 'chasuble' || g === 'apron') {
      // Panels: front (and back) plates hanging from the shoulders; the cross / division in the other tincture.
      const top = torsoRingAt(ctx, g === 'apron' ? 0.8 : 0.93);
      const ySpan: V3 = [0, hemY, 0];
      for (const z of g === 'apron' ? [1] : [1, -1]) {
        const w = (g === 'chasuble' ? 1.15 : 0.9) * (R0.r + off);
        const zf = (y: number): number => {
          const t = Math.max(0, Math.min(1, (y - torsoRingAt(ctx, 0).y) / (top.y - torsoRingAt(ctx, 0).y)));
          const R = torsoRingAt(ctx, t);
          return z * (R.r * 0.8 + off + (z > 0 ? R.bellyAmt : 0)) + z * 0.004;
        };
        const N = 4;
        const pts: V3[] = [];
        for (let i = 0; i <= N; i++) { const y = top.y + (ySpan[1] - top.y) * (i / N); pts.push([0, y, Math.max(z * 0.02, z * Math.abs(zf(Math.max(y, torsoRingAt(ctx, 0).y)))) + (y < torsoRingAt(ctx, 0).y ? z * 0.02 * ((torsoRingAt(ctx, 0).y - y) / 0.2) : 0)]); }
        const widths = pts.map((_, i) => w * (0.75 + 0.3 * (i / N)));
        strip(s, pts, widths, [1, 0, 0], (i) => (i < 2 ? rigid(ctx.bi('spine_02')) : (skirtRows[Math.min(rowsN, i - 1)]?.skin(0.25, pts[i]) ?? rigid(pelvis))),
          { mat: g === 'apron' ? MAT.leather : g === 'chasuble' ? MAT.dye0 : MAT.tinctA, region: REG.torso, lw: 1.1 });
        if (g === 'tabard' || g === 'chasuble') {
          // the cross (tabard: tincture B; chasuble: the gold Y-orphrey)
          const cm = g === 'tabard' ? MAT.tinctB : metal;
          const zz = (i: number): V3 => add(pts[i], [0, 0, z * 0.003]);
          strip(s, [zz(0), zz(1), zz(2), zz(3), zz(4)], [0.012, 0.012, 0.012, 0.012, 0.012], [1, 0, 0], (i) => (i < 2 ? rigid(ctx.bi('spine_02')) : rigid(pelvis)), { mat: cm, region: REG.extra, lw: 0.6, hull: false });
          const yc = pts[1][1] - 0.03;
          const zc = zf(yc) + z * 0.004;
          strip(s, [[-w * 0.6, yc, zc], [0, yc, zc + z * 0.002], [w * 0.6, yc, zc]], [0.012, 0.012, 0.012], [0, 1, 0], () => rigid(ctx.bi('spine_02')), { mat: cm, region: REG.extra, lw: 0.6, hull: false });
        }
      }
      if (g !== 'apron') return;
    }
    if (hemY < R0.y - 0.02) arcSheet(s, skirtRows, 0, 1, ctx.cfg.torsoSides, g === 'rags' ? MAT.dye0 : mat === MAT.tinctA ? MAT.dye0 : mat, g === 'motley' ? MAT.dye1 : MAT.dye1, REG.torso);
    // Belt (rope / leather) at the waist.
    if (['robe', 'tunic', 'gambeson', 'smock', 'rags', 'mail', 'execTunic', 'alb'].includes(g)) {
      const W = torsoRingAt(ctx, 0.2);
      const bm = g === 'robe' || g === 'alb' || g === 'rags' ? MAT.parchment : MAT.leather;
      loft(s, [{ c: [0, W.y - 0.008, 0.004], rs: W.r + off + 0.004, rf: W.r * 0.8 + off + 0.004, skin: rigid(pelvis), deform: (p, th) => torsoDeform(p, th, W.bellyAmt * 1.05, 0) },
        { c: [0, W.y + 0.01, 0.004], rs: W.r + off + 0.004, rf: W.r * 0.8 + off + 0.004, skin: rigid(pelvis), deform: (p, th) => torsoDeform(p, th, W.bellyAmt * 1.05, 0) }],
      { sides: ctx.cfg.torsoSides, front: [0, 0, 1], mat: bm, region: REG.extra, tangent: () => [0, 1, 0] });
      if (g === 'robe') strip(s, [[0.03, W.y - 0.01, W.r * 0.8 + W.bellyAmt + off + 0.008], [0.035, W.y - 0.12, W.r * 0.8 + W.bellyAmt + off + 0.012]], [0.005, 0.004], [1, 0, 0], () => rigid(pelvis), { mat: MAT.parchment, region: REG.extra, lw: 0.7 });
    }
    // Sleeves.
    if (!spec.flags.wingArms && g !== 'apron' && g !== 'chasuble' && g !== 'sack') {
      const longSleeve = ['robe', 'alb', 'gambeson', 'motley', 'ermine', 'smock', 'mail'].includes(g);
      for (const side of [1, -1] as const) sleeve(ctx, s, side, longSleeve, g === 'robe' || g === 'alb' ? 1.45 : 1.12, off, g === 'motley' ? (side > 0 ? MAT.dye1 : MAT.dye0) : mat === MAT.tinctA ? MAT.dye0 : mat);
    }
  });
}

function sleeve(ctx: BuildCtx, s: Surface, side: 1 | -1, long: boolean, flare: number, off: number, mat: number): void {
  const S = side > 0 ? 'L' : 'R';
  const ua = ctx.bi(`upperarm_${S}` as BoneName), fa = ctx.bi(`forearm_${S}` as BoneName), cl = ctx.bi(`clavicle_${S}` as BoneName);
  const pu = ctx.P[`upperarm_${S}` as BoneName]!, pf = ctx.P[`forearm_${S}` as BoneName]!, ph = ctx.P[`hand_${S}` as BoneName]!;
  const r = ctx.d.armR;
  const start: V3 = [side * Math.max(0.035, pu[0] * 0.45), pu[1] + 0.014, pu[2]];
  const pts: V3[] = long ? [start, pu, pf, lerp3(pf, ph, 0.85)] : [start, pu, lerp3(pu, pf, 0.7)];
  const rad = long ? [1.15, 1.12, 1.0, flare] : [1.15, 1.12, 1.1];
  const rings: Ring[] = pts.map((p, i) => ({ c: p, rs: r * rad[i] + off, rf: r * rad[i] * 0.95 + off, lw: 1,
    skin: i === 0 ? blend(cl, ua, 0.6) : p === pf ? blend(ua, fa, 0.5) : i >= 3 ? rigid(fa) : rigid(ua) }));
  loft(s, rings, { sides: Math.max(5, ctx.cfg.limbSides), front: [0, 0, 1], mat, region: REG.upperArm, capStart: 'point', capStartLen: r * 0.5, capEnd: 'none', creases: [{ ring: pts.length - 1 }] });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── capes ──
function cape(ctx: BuildCtx): void {
  const { spec, d } = ctx;
  const c = spec.o.cape;
  if (c === 'none') return;
  const cloth = ctx.rig.chains.cloth;
  const sp2 = ctx.bi('spine_02');
  const top = torsoRingAt(ctx, 0.93);
  const pelvisY = ctx.P.pelvis![1];
  const short = c === 'capelet';
  const hemY = short ? top.y - 0.17 : c === 'blanket' ? pelvisY - 0.05 : Math.max(0.07, pelvisY * 0.28);
  const rows = Math.max(2, ctx.cfg.clothRows);
  const outer = c === 'ermine' ? MAT.parchment : c === 'crusader' ? MAT.tinctA : MAT.dye0;
  const inner = c === 'ermine' ? MAT.dye0 : MAT.dye1;
  part(ctx, 'garb.cape', (s) => {
    const rws: Array<{ c: V3; rs: number; rf: number; skin: (th: number, p: V3) => Skin; lw?: number; deform?: (p: V3, th: number) => V3 }> = [];
    for (let k = 0; k <= rows; k++) {
      const u = k / rows;
      const y = top.y + 0.012 + (hemY - top.y) * u;
      const r = (top.r + 0.03 + (short ? 0.05 : 0.09) * u) * Math.max(1, d.chestW * 0.9);
      const tat = c === 'tattered' && k === rows;
      rws.push({
        c: [0, y, -0.02 - 0.03 * u], rs: r * (k === 0 ? 0.85 : 1.08), rf: r * (k === 0 ? 0.8 : 0.85), lw: k === rows ? 1.2 : 1,
        deform: tat ? (p, th) => [p[0], p[1] + (Math.sin(th * 53) > 0.2 ? 0.05 : 0), p[2]] : undefined,
        skin: (_th, p) => {
          if (cloth.length >= 4) { const left = p[0] >= 0; return u < 0.35 ? blend(sp2, cloth[left ? 0 : 1], u / 0.35) : blend(cloth[left ? 0 : 1], cloth[left ? 2 : 3], Math.min(1, (u - 0.35) / 0.5)); }
          if (cloth.length >= 2) return blend(sp2, cloth[p[0] >= 0 ? 0 : 1], Math.min(1, u * 1.5));
          return rigid(sp2);
        },
      });
    }
    // Around the back: θ from the left shoulder (0.47) through the back (0.75) to the right shoulder (1.03).
    const cols = Math.max(4, ctx.cfg.clothCols * 3);
    arcSheet(s, rws, short ? 0.36 : 0.44, short ? 1.14 : 1.06, cols, outer, inner, REG.extra);
    if (c === 'count') {
      // high standing collar
      const yc = top.y + 0.02;
      const rr: Array<{ c: V3; rs: number; rf: number; skin: (th: number, p: V3) => Skin }> = [0, 1].map((k) => ({ c: [0, yc + k * 0.1, -0.02 - 0.03 * k] as V3, rs: top.r + 0.03 + k * 0.05, rf: top.r * 0.9 + k * 0.05, skin: () => rigid(sp2) }));
      arcSheet(s, rr, 0.5, 1.0, 6, outer, inner, REG.extra);
    }
    if (c === 'crusader') {
      const yc = top.y - 0.12;
      const zc = -(top.r * 0.9 + 0.06);
      strip(s, [[0, yc + 0.08, zc], [0, yc - 0.12, zc - 0.01]], [0.016, 0.016], [1, 0, 0], () => rigid(sp2), { mat: MAT.tinctB, region: REG.extra, hull: false, lw: 0.5 });
      strip(s, [[-0.06, yc, zc - 0.004], [0.06, yc, zc - 0.004]], [0.016, 0.016], [0, 1, 0], () => rigid(sp2), { mat: MAT.tinctB, region: REG.extra, hull: false, lw: 0.5 });
    }
    // clasp
    const metal = METAL_MAT[spec.f.metal] ?? MAT.gold;
    const cy = top.y + 0.004;
    box(s, [0, cy, top.r * 0.8 + 0.012], [0.012, 0, 0], [0, 0.012, 0], [0, 0, 0.005], rigid(sp2), { mat: metal, region: REG.extra });
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── headwear ──
function headwear(ctx: BuildCtx): void {
  const { spec, d } = ctx;
  const hw = spec.o.headwear;
  if (hw === 'none') return;
  const head = ctx.bi('head');
  const hat1 = ctx.ob('hat_1');
  const prof = ctx.rig.skull;
  const metal = METAL_MAT[spec.f.metal] ?? MAT.gold;
  const crown = d.crown;
  const H = HAT_HEIGHT[hw] ?? 0.05;
  const ringAt = (f: number, grow: number): { c: V3; rs: number; rf: number } => { const r = prof.ring(f); return { c: [0, r.y, r.cz], rs: r.rs * grow + 0.006, rf: r.rf * grow + 0.006 }; };
  const hr = rigid(head);
  part(ctx, 'garb.headwear', (s) => {
    switch (hw) {
      case 'crown': case 'circlet': {
        const b = ringAt(0.84, 1.0);
        const h = hw === 'crown' ? 0.03 : 0.012;
        const rs: Ring[] = [{ c: b.c, rs: b.rs * 0.92, rf: b.rf * 0.92, skin: hr }, { c: add(b.c, [0, h, 0]), rs: b.rs * 0.96, rf: b.rf * 0.96, skin: hr }];
        loft(s, rs, { sides: 10, front: [0, 0, 1], mat: MAT.gold, region: REG.extra, tangent: () => [0, 1, 0], creases: [{ ring: 0 }, { ring: 1 }] });
        if (hw === 'crown') {
          for (let k = 0; k < 6; k++) {
            const a = (k / 6) * TAU + TAU * 0.25 / 6;
            const p: V3 = [b.c[0] + Math.cos(a) * b.rs * 0.96, b.c[1] + h, b.c[2] + Math.sin(a) * b.rf * 0.96];
            const tip = add(p, [0, 0.04, 0]);
            const t2 = nrm([Math.cos(a + Math.PI / 2), 0, Math.sin(a + Math.PI / 2)]);
            strip(s, [p, tip], [0.016, 0.001], t2, () => hr, { mat: MAT.gold, region: REG.extra, lw: 0.8 });
          }
          box(s, [b.c[0], b.c[1] + h * 0.5, b.c[2] + b.rf * 0.97], [0.008, 0, 0], [0, 0.008, 0], [0, 0, 0.004], hr, { mat: MAT.gem, region: REG.extra });
        }
        break;
      }
      case 'mitre': {
        // two pointed-arch plates (front/back) meeting at the sides, cleft at the top; gold orphrey bands
        const b = ringAt(0.86, 1.02);
        const rs: Ring[] = [];
        for (let k = 0; k <= 4; k++) {
          const u = k / 4;
          rs.push({ c: add(b.c, [0, u * 0.2, -0.01 * u]), rs: b.rs * (1 - 0.92 * Math.pow(u, 1.6)), rf: b.rf * (0.95 - 0.75 * u), skin: hr, lw: 1 });
        }
        loft(s, rs, { sides: 8, front: [0, 0, 1], mat: MAT.dye0, region: REG.extra, tangent: () => [0, 1, 0], capEnd: 'point', capEndLen: 0.02, creases: [{ ring: 0 }] });
        strip(s, [add(b.c, [0, 0, b.rf * 0.96]), add(b.c, [0, 0.19, b.rf * 0.24])], [0.012, 0.004], [1, 0, 0], () => hr, { mat: metal, region: REG.extra, hull: false, lw: 0.5 });
        loft(s, [{ c: b.c, rs: b.rs * 1.01, rf: b.rf * 1.01, skin: hr }, { c: add(b.c, [0, 0.022, 0]), rs: b.rs * 0.97, rf: b.rf * 0.97, skin: hr }],
          { sides: 8, front: [0, 0, 1], mat: metal, region: REG.extra, tangent: () => [0, 1, 0] });
        for (const sx of [1, -1]) strip(s, [add(b.c, [sx * 0.03, 0.01, -b.rf * 0.95]), add(b.c, [sx * 0.035, -0.16, -b.rf * 1.05])], [0.012, 0.012], [1, 0, 0], () => (hat1 >= 0 ? rigid(hat1) : hr), { mat: MAT.dye0, region: REG.extra });
        break;
      }
      case 'greatHelm': case 'pot': {
        const lo = prof.ring(0.02), hi = prof.ring(0.86);
        const R = Math.max(lo.rs, hi.rs, prof.ring(0.45).rs) * 1.08 + 0.008;
        const Rz = Math.max(lo.rf, prof.ring(0.45).rf) * 1.06 + 0.012;
        const y0 = lo.y - 0.015, y1 = crown + (hw === 'pot' ? 0.02 : -0.01);
        const rs: Ring[] = [0, 0.5, 1].map((u) => ({ c: [0, y0 + (y1 - y0) * u, lo.cz] as V3, rs: R * (hw === 'pot' ? 1.06 : 1 - 0.04 * u), rf: Rz * (1 - 0.04 * u), skin: hr, lw: 1 }));
        loft(s, rs, { sides: 8, front: [0, 0, 1], mat: MAT.iron, region: REG.extra, tangent: () => [0, 1, 0], capEnd: 'flat', creases: [{ ring: 1, from: 0.15, to: 0.35 }] });
        if (hw === 'greatHelm') {
          // eye slit (ink) and a cross of breaths; gold rim
          const ys = ctx.d.eyeY + 0.004;
          strip(s, [[-R * 0.75, ys, Rz + lo.cz + 0.002], [R * 0.75, ys, Rz + lo.cz + 0.002]], [0.007, 0.007], [0, 1, 0], () => hr, { mat: MAT.ink, region: REG.extra, hull: false, lw: 0 });
          strip(s, [[0, ys - 0.012, Rz + lo.cz + 0.003], [0, y0 + 0.02, Rz + lo.cz + 0.003]], [0.006, 0.006], [1, 0, 0], () => hr, { mat: metal, region: REG.extra, hull: false, lw: 0.4 });
        } else {
          strip(s, [[R * 0.9, y1 - 0.01, lo.cz], [R * 1.35, y1 - 0.015, lo.cz - 0.01], [R * 1.6, y1 - 0.02, lo.cz]], [0.008, 0.008, 0.008], [0, 1, 0], () => hr, { mat: MAT.iron, region: REG.extra });
        }
        break;
      }
      case 'kettle': {
        const b = ringAt(0.8, 1.04);
        loft(s, [{ c: b.c, rs: b.rs * 1.75, rf: b.rf * 1.75, skin: hr }, { c: add(b.c, [0, 0.012, 0]), rs: b.rs * 1.05, rf: b.rf * 1.05, skin: hr },
          { c: add(b.c, [0, 0.05, 0]), rs: b.rs * 0.8, rf: b.rf * 0.8, skin: hr }], { sides: 10, front: [0, 0, 1], mat: MAT.iron, region: REG.extra, tangent: () => [0, 1, 0], capStart: 'flat', capEnd: 'point', capEndLen: 0.03 });
        break;
      }
      case 'bascinet': {
        // pointed dome down to the brow; beak visor on the hat chain (it flips in a fidget)
        const lo = ringAt(0.42, 1.08), mid = ringAt(0.72, 1.06);
        loft(s, [{ c: lo.c, rs: lo.rs, rf: lo.rf, skin: hr }, { c: mid.c, rs: mid.rs, rf: mid.rf, skin: hr }, { c: [0, crown + 0.02, lo.c[2] - 0.02], rs: lo.rs * 0.55, rf: lo.rf * 0.55, skin: hr }],
          { sides: 10, front: [0, 0, 1], mat: MAT.iron, region: REG.extra, tangent: () => [0, 1, 0], capEnd: 'point', capEndLen: 0.07, capStart: 'none' });
        const vb = hat1 >= 0 ? rigid(hat1) : hr;
        const fz = ctx.skullFront!(0, ctx.d.eyeY) + 0.03;
        const vc: V3 = [0, ctx.d.eyeY - 0.005, fz];
        loft(s, [{ c: add(vc, [0, 0, -0.04]), rs: lo.rs * 0.9, rf: 0.05, skin: vb }, { c: add(vc, [0, -0.01, 0.03]), rs: 0.045, rf: 0.03, skin: vb }],
          { sides: 6, front: [0, 1, 0], mat: MAT.iron, region: REG.extra, capEnd: 'point', capEndLen: 0.05, tangent: () => [0, -0.15, 1] });
        for (let k = 0; k < 3; k++) strip(s, [add(vc, [-0.03, 0.012 - k * 0.012, 0.035]), add(vc, [0.03, 0.012 - k * 0.012, 0.035])], [0.003, 0.003], [0, 1, 0], () => vb, { mat: MAT.ink, region: REG.extra, hull: false, lw: 0 });
        break;
      }
      case 'cowl': case 'coif': case 'execHood': {
        // hood shell around the skull with the face opening; a back point; capelet ring on the shoulders
        const rowsF = hw === 'coif' ? [0.1, 0.45, 0.8, 0.97] : [-0.1, 0.25, 0.6, 0.9, 1.0];
        const grow = hw === 'coif' ? 1.04 : 1.14;
        const rows = rowsF.map((f) => {
          const r = prof.ring(Math.max(0, Math.min(0.97, f)));
          const extra = f > 0.96 && hw !== 'coif' ? 0.03 : 0;
          return { c: [0, f < 0 ? d.chin - 0.04 : r.y + extra, r.cz - 0.01] as V3, rs: (f < 0 ? d.skullRx * 0.85 : r.rs) * grow + 0.01, rf: (f < 0 ? d.skullRz * 0.9 : r.rf) * grow + 0.008, skin: () => hr, lw: 1 };
        });
        const open = hw === 'coif' ? 0.16 : 0.2;
        arcSheet(s, rows, 0.25 + open, 1.25 - open, 10, hw === 'execHood' ? MAT.dye0 : MAT.dye0, MAT.dye1, REG.extra);
        if (hw === 'execHood') {
          const topR = prof.ring(0.96);
          cone(s, [0, topR.y + 0.01, topR.cz - 0.01], [0, 1, -0.15], topR.rs * 0.9, 0.2, hr, MAT.dye0, 8);
        } else if (hw === 'cowl') {
          const back = prof.ring(0.6);
          cone(s, [0, back.y, back.cz - back.rf * 1.05], [0, -0.6, -1], 0.04, 0.12, hr, MAT.dye0, 5);
        }
        break;
      }
      case 'chaperon': {
        // padded roll + draped crest + liripipe (concept: the toad's / the fox merchant's chaperon)
        const b = ringAt(0.82, 1.05);
        const rs: Ring[] = [-0.7, 0, 0.7].map((t) => ({ c: add(b.c, [0, 0.025 + t * 0.02, 0]), rs: b.rs * (1 + 0.12 * Math.cos(t)), rf: b.rf * (1 + 0.12 * Math.cos(t)), skin: hr }));
        loft(s, rs, { sides: 10, front: [0, 0, 1], mat: MAT.dye0, region: REG.extra, tangent: () => [0, 1, 0], capEnd: 'point', capEndLen: 0.03, jitter: 0.04, seed: 5 });
        strip(s, [add(b.c, [b.rs * 0.6, 0.05, -b.rf * 0.3]), add(b.c, [b.rs * 1.1, -0.02, -b.rf * 0.5]), add(b.c, [b.rs * 1.05, -0.14, -b.rf * 0.7])], [0.03, 0.025, 0.02], [0, 0, 1], () => (hat1 >= 0 ? rigid(hat1) : hr), { mat: MAT.dye0, region: REG.extra });
        strip(s, [add(b.c, [-b.rs * 0.3, 0.04, -b.rf]), add(b.c, [-b.rs * 0.4, -0.1, -b.rf * 1.2]), add(b.c, [-b.rs * 0.3, -0.25, -b.rf * 1.1])], [0.012, 0.01, 0.006], [1, 0, 0], () => hr, { mat: MAT.dye0, region: REG.extra });
        break;
      }
      case 'beret': {
        const b = ringAt(0.86, 1.04);
        loft(s, [{ c: b.c, rs: b.rs * 0.98, rf: b.rf * 0.98, skin: hr }, { c: add(b.c, [0.02, 0.03, 0]), rs: b.rs * 1.25, rf: b.rf * 1.25, skin: hr }, { c: add(b.c, [0.03, 0.05, 0]), rs: b.rs * 0.6, rf: b.rf * 0.6, skin: hr }],
          { sides: 10, front: [0, 0, 1], mat: MAT.dye0, region: REG.extra, tangent: () => [0.2, 1, 0], capEnd: 'point', capEndLen: 0.01 });
        break;
      }
      case 'jester': {
        const b = ringAt(0.8, 1.05);
        loft(s, [{ c: b.c, rs: b.rs, rf: b.rf, skin: hr }, { c: add(b.c, [0, 0.04, 0]), rs: b.rs * 0.9, rf: b.rf * 0.9, skin: hr }], { sides: 8, front: [0, 0, 1], mat: MAT.dye0, region: REG.extra, tangent: () => [0, 1, 0], capEnd: 'flat' });
        for (const sx of [1, -1]) {
          const base = add(b.c, [sx * b.rs * 0.5, 0.04, 0]);
          const pts: V3[] = [base, add(base, [sx * 0.06, 0.08, 0]), add(base, [sx * 0.14, 0.06, 0.01]), add(base, [sx * 0.16, -0.02, 0.02])];
          const rs: Ring[] = pts.map((p, i) => ({ c: p, rs: 0.03 * (1 - i / 3.4), rf: 0.03 * (1 - i / 3.4), skin: hat1 >= 0 && i > 1 ? rigid(hat1) : hr }));
          loft(s, rs, { sides: 5, front: [0, 0, 1], mat: sx > 0 ? MAT.dye0 : MAT.dye1, region: REG.extra, capEnd: 'point', capEndLen: 0.01 });
          box(s, add(pts[3], [0, -0.012, 0]), [0.008, 0, 0], [0, 0.008, 0], [0, 0, 0.008], hat1 >= 0 ? rigid(hat1) : hr, { mat: MAT.gold, region: REG.extra });
        }
        break;
      }
      case 'laurel': case 'daisies': {
        const b = ringAt(0.8, 1.04);
        for (let k = 0; k < 10; k++) {
          const a = (k / 10) * TAU;
          const p: V3 = [b.c[0] + Math.cos(a) * b.rs, b.c[1], b.c[2] + Math.sin(a) * b.rf];
          const t2: V3 = [-Math.sin(a), 0, Math.cos(a)];
          strip(s, [p, add(p, add(scl(t2, 0.03), [0, 0.012, 0]))], [0.01, 0.002], [0, 1, 0], () => hr, { mat: hw === 'laurel' ? MAT.gold : MAT.parchment, region: REG.extra, lw: 0.6 });
        }
        break;
      }
      case 'haloDisc': {
        const c: V3 = [0, crown - 0.06, d.headZ - d.skullRz * 1.05];
        fan(s, c, ellipse(c, [1, 0, 0], [0, 1, 0], 0.17, 0.17, 12).reverse(), hr, { mat: MAT.gold, region: REG.extra, lw: 0.8 });
        fan(s, add(c, [0, 0, -0.002]), ellipse(add(c, [0, 0, -0.002]), [1, 0, 0], [0, 1, 0], 0.17, 0.17, 12), hr, { mat: MAT.gold, region: REG.extra, lw: 0.8, hull: false });
        break;
      }
      case 'pilgrim': case 'nemesisHat': case 'astrologer': case 'hennin': {
        const b = ringAt(0.82, 1.03);
        const brim = hw === 'pilgrim' ? 2.0 : hw === 'nemesisHat' ? 1.7 : 1.0;
        const tallH = hw === 'astrologer' ? 0.26 : hw === 'hennin' ? 0.32 : 0.07;
        const tilt: V3 = hw === 'hennin' ? [0, 1, -0.45] : hw === 'nemesisHat' ? [0.15, 1, -0.05] : [0, 1, -0.05];
        if (brim > 1.01) loft(s, [{ c: b.c, rs: b.rs * brim, rf: b.rf * brim, skin: hr, deform: hw === 'nemesisHat' ? (p) => [p[0], p[1] + (p[2] > b.c[2] ? 0.02 : -0.01), p[2]] : undefined }, { c: add(b.c, [0, 0.008, 0]), rs: b.rs * 1.02, rf: b.rf * 1.02, skin: hr }],
          { sides: 10, front: [0, 0, 1], mat: hw === 'pilgrim' ? MAT.leather : MAT.leather, region: REG.extra, tangent: () => [0, 1, 0], capStart: 'flat' });
        cone(s, b.c, tilt, b.rs * 1.02, tallH + 0.03, hat1 >= 0 && hw !== 'pilgrim' ? hr : hr, hw === 'astrologer' ? MAT.dye1 : hw === 'hennin' ? MAT.dye0 : MAT.leather, 8);
        if (hw === 'pilgrim') box(s, add(b.c, [0, 0.035, b.rf * 1.05]), [0.016, 0, 0], [0, 0.016, 0], [0, 0, 0.005], hr, { mat: MAT.shell, region: REG.extra });
        if (hw === 'nemesisHat') strip(s, [add(b.c, [b.rs * 0.6, 0.04, -b.rf * 0.2]), add(b.c, [b.rs * 1.2, 0.14, -b.rf * 0.8])], [0.012, 0.004], [0, 0, 1], () => (hat1 >= 0 ? rigid(hat1) : hr), { mat: MAT.dye0, region: REG.extra });
        if (hw === 'hennin') strip(s, [add(b.c, [0, 0.3, -0.13]), add(b.c, [0, 0.05, -0.25]), add(b.c, [0, -0.25, -0.2])], [0.02, 0.06, 0.07], [1, 0, 0], () => (hat1 >= 0 ? rigid(hat1) : hr), { mat: MAT.parchment, region: REG.extra });
        if (hw === 'astrologer') for (let k = 0; k < 4; k++) { const a = k * 1.7; box(s, add(b.c, [Math.cos(a) * b.rs * 0.7 * (1 - k * 0.15), 0.05 + k * 0.045, Math.sin(a) * b.rf * 0.7 * (1 - k * 0.15) + 0.02]), [0.007, 0, 0], [0, 0.007, 0], [0, 0, 0.003], hr, { mat: MAT.gold, region: REG.extra }); }
        break;
      }
      case 'plagueMask': {
        // leather beak mask over the snout + goggles (gilded rim) + a small brimmed hat
        const fz = ctx.skullFront!(0, ctx.d.mouthY + 0.03) - 0.01;
        const bc: V3 = [0, ctx.d.mouthY + 0.025, fz];
        cone(s, bc, [0, -0.35, 1], 0.05, 0.2, hr, MAT.leather, 6);
        for (const sx of [1, -1]) {
          const g = ctx.rig.eyes[sx > 0 ? 0 : 1];
          const c = add(g.center as V3, [0, 0, g.dome + 0.008]);
          loft(s, [{ c, rs: g.rx * 1.25, rf: g.rx * 1.25, skin: hr }, { c: add(c, [0, 0, 0.008]), rs: g.rx * 1.25, rf: g.rx * 1.25, skin: hr }], { sides: 8, front: [0, 1, 0], mat: MAT.gold, region: REG.extra, tangent: () => [0, 0, 1] });
          fan(s, add(c, [0, 0, 0.006]), ellipse(add(c, [0, 0, 0.006]), [1, 0, 0], [0, 1, 0], g.rx * 1.1, g.rx * 1.1, 8), hr, { mat: MAT.ink, region: REG.extra, hull: false, lw: 0 });
        }
        const b = ringAt(0.8, 1.04);
        loft(s, [{ c: b.c, rs: b.rs * 1.6, rf: b.rf * 1.6, skin: hr }, { c: add(b.c, [0, 0.01, 0]), rs: b.rs, rf: b.rf, skin: hr }, { c: add(b.c, [0, 0.07, 0]), rs: b.rs * 0.92, rf: b.rf * 0.92, skin: hr }],
          { sides: 10, front: [0, 0, 1], mat: MAT.ink, region: REG.extra, tangent: () => [0, 1, 0], capStart: 'flat', capEnd: 'flat' });
        break;
      }
      default: {
        const b = ringAt(0.82, 1.04);
        loft(s, [{ c: b.c, rs: b.rs, rf: b.rf, skin: hr }, { c: add(b.c, [0, H, 0]), rs: b.rs * 0.7, rf: b.rf * 0.7, skin: hr }], { sides: 8, front: [0, 0, 1], mat: MAT.dye0, region: REG.extra, tangent: () => [0, 1, 0], capEnd: 'flat' });
      }
    }
  });
}

/** A loft cone from `base` along `dir` (radius → 0). */
function cone(s: Surface, base: V3, dir: V3, r: number, L: number, skin: Skin, mat: number, sides = 6): void {
  const dd = nrm(dir);
  loft(s, [{ c: base, rs: r, rf: r, skin }, { c: add(base, scl(dd, L * 0.55)), rs: r * 0.55, rf: r * 0.55, skin }],
    { sides, front: Math.abs(dd[1]) > 0.9 ? [0, 0, 1] : [0, 1, 0], mat, region: REG.extra, capStart: 'flat', capEnd: 'point', capEndLen: L * 0.45 });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── wings ──
function wings(ctx: BuildCtx): void {
  const w = ctx.spec.o.wings;
  if (w === 'none') return;
  const bones = ctx.rig.chains.wings;
  part(ctx, 'garb.wings', (s) => {
    for (const side of [1, -1] as const) {
      const bone = bones.length === 2 ? bones[side > 0 ? 0 : 1] : ctx.bi('spine_02');
      const root = ctx.P[side > 0 ? 'wing_L' : 'wing_R'] ?? ([side * 0.05, ctx.P.spine_02![1] + 0.03, -0.09] as V3);
      const mat = w === 'angel' || w === 'owl' ? MAT.plumage : w === 'bat' ? MAT.leather : MAT.parchment;
      const n = w === 'angel' ? 6 : 4;
      for (let k = 0; k < n; k++) {
        const a = (25 + k * (70 / (n - 1))) * DEG;
        const dir = nrm([side * Math.cos(a) * 0.9, Math.sin(a) * 0.9 - 0.25, -0.35]);
        const L = (w === 'angel' ? 0.32 : 0.24) * (1 - 0.12 * k / n);
        strip(s, [root, add(root, scl(dir, L * 0.5)), add(root, scl(dir, L))], [0.03, 0.035, 0.006], nrm(cross(dir, [0, 0, 1])), () => rigid(bone), { mat, region: REG.extra, lw: 0.9 });
      }
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── footwear ──
function footwear(ctx: BuildCtx): void {
  const { spec } = ctx;
  const fw = spec.o.footwear;
  if (fw === 'none' || spec.flags.slugBody) return;
  part(ctx, 'garb.footwear', (s) => {
    for (const side of [1, -1] as const) {
      const S = side > 0 ? 'L' : 'R';
      const ca = ctx.bi(`calf_${S}` as BoneName), ft = ctx.bi(`foot_${S}` as BoneName), toe = ctx.bi(`toe_${S}` as BoneName);
      const pc = ctx.P[`calf_${S}` as BoneName]!, pf = ctx.P[`foot_${S}` as BoneName]!, pt = ctx.P[`toe_${S}` as BoneName]!;
      const r = ctx.d.legR;
      const mat = fw === 'sabatons' ? MAT.iron : fw === 'clogs' ? MAT.wood : fw === 'wraps' ? MAT.parchment : fw === 'poulaines' ? MAT.dye0 : MAT.leather;
      const tall = fw === 'cavalier' || fw === 'spurred' || fw === 'wraps';
      if (tall) {
        const top = lerp3(pc, pf, -0.15);
        loft(s, [{ c: top, rs: r * 1.05, rf: r * 1.05, skin: blend(ca, ca, 0) }, { c: lerp3(pc, pf, 0.45), rs: r * 0.82, rf: r * 0.82, skin: rigid(ca) }, { c: pf, rs: r * 0.75, rf: r * 0.75, skin: blend(ca, ft, 0.5) }],
          { sides: 6, front: [0, 0, 1], mat, region: REG.shin, capStart: 'none', creases: [{ ring: 0 }] });
        if (fw !== 'wraps') loft(s, [{ c: add(top, [0, -0.01, 0]), rs: r * 1.05, rf: r * 1.05, skin: rigid(ca) }, { c: add(top, [0, 0.03, 0.01]), rs: r * 1.5, rf: r * 1.45, skin: rigid(ca) }],
          { sides: 6, front: [0, 0, 1], mat, region: REG.shin, capStart: 'none', capEnd: 'none' });
      }
      // the shoe: a slightly bigger copy of the foot wedge, flat sole at 0
      const fs = ctx.d.footLen / 0.11;
      const heelZ = pf[2] - 0.026, toeZ = pt[2] + (fw === 'poulaines' ? 0.11 : 0.034) * fs;
      const zs = [heelZ, (heelZ + pt[2]) / 2, pt[2], toeZ];
      const hs = [0.024, 0.027, 0.022, fw === 'poulaines' ? 0.004 : 0.013];
      const ws = [0.8, 1.0, 1.08, fw === 'poulaines' ? 0.15 : 0.9];
      const w = 0.029;
      const rings: Ring[] = zs.map((z, i) => ({ c: [pt[0], hs[i] + (fw === 'poulaines' && i === 3 ? 0.03 : 0), z] as V3, rs: w * ws[i], rf: hs[i] + 0.003, skin: i <= 1 ? rigid(ft) : rigid(toe), lw: 1.1,
        deform: (p: V3) => [p[0], Math.max(0, p[1]), p[2]] as V3 }));
      loft(s, rings, { sides: 6, front: [0, 1, 0], mat, region: REG.shin, capStart: 'flat', capEnd: 'point', capEndLen: 0.01, tangent: () => [0, 0, 1] });
      if (fw === 'spurred') box(s, [pt[0], 0.025, heelZ - 0.012], [0.006, 0, 0], [0, 0.006, 0], [0, 0, 0.01], rigid(ft), { mat: MAT.gold, region: REG.extra });
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── belt / neck / props ──
function beltItems(ctx: BuildCtx): void {
  const { spec } = ctx;
  const items: Array<[string, 'belt_L' | 'belt_R']> = [[spec.o.belt1, 'belt_L'], [spec.o.belt2, 'belt_R']];
  if (items.every(([i]) => i === 'none')) return;
  part(ctx, 'garb.belt', (s) => {
    for (const [it, sock] of items) {
      if (it === 'none') continue;
      const so = ctx.rig.rig.sockets[sock]!;
      const bone = so.bone;
      const piv = ctx.rig.rig.bones[bone].pivot;
      const at: V3 = scl([piv[0] + so.offset[0], piv[1] + so.offset[1], piv[2] + so.offset[2]], 1 / ctx.rig.norm);
      const out = Math.sign(at[0]) || 1;
      const sk = rigid(bone);
      const p = add(at, [out * 0.01, -0.02, 0.01]);
      switch (it) {
        case 'pouch': box(s, p, [0.022, 0, 0], [0, 0.026, 0], [0, 0, 0.014], sk, { mat: MAT.leather, region: REG.extra }); break;
        case 'dagger':
          box(s, add(p, [0, 0.01, 0]), [0.006, 0, 0], [0, 0.02, 0], [0, 0, 0.006], sk, { mat: MAT.leather, region: REG.extra });
          box(s, add(p, [0, -0.045, 0]), [0.008, 0, 0], [0, 0.04, 0], [0, 0, 0.003], sk, { mat: MAT.iron, region: REG.extra }); break;
        case 'keys': for (let k = 0; k < 3; k++) box(s, add(p, [(k - 1) * 0.008, -0.01 - k * 0.004, 0]), [0.003, 0, 0], [0, 0.018, 0], [0, 0, 0.002], sk, { mat: MAT.iron, region: REG.extra }); break;
        case 'book': box(s, p, [0.024, 0, 0], [0, 0.03, 0], [0, 0, 0.01], sk, { mat: MAT.dye1, region: REG.extra }); break;
        case 'flask': cone(s, add(p, [0, -0.03, 0]), [0, 1, 0], 0.018, 0.06, sk, MAT.leather, 6); break;
        case 'scroll': box(s, p, [0.008, 0, 0], [0, 0.03, 0], [0, 0, 0.008], sk, { mat: MAT.parchment, region: REG.extra }); break;
        default: box(s, p, [0.014, 0, 0], [0, 0.014, 0], [0, 0, 0.004], sk, { mat: it === 'scallop' ? MAT.shell : MAT.silver, region: REG.extra });
      }
    }
  });
}

function neckItem(ctx: BuildCtx): void {
  const { spec } = ctx;
  const it = spec.o.neckAcc;
  if (it === 'none') return;
  const sp2 = ctx.bi('spine_02'), head = ctx.bi('head');
  const top = torsoRingAt(ctx, 0.96);
  const metal = METAL_MAT[spec.f.metal] ?? MAT.gold;
  part(ctx, 'garb.neck', (s) => {
    if (it === 'rosary' || it === 'cross' || it === 'amulet' || it === 'bell') {
      const n = 10;
      const pts: V3[] = [];
      for (let k = 0; k <= n; k++) {
        const a = Math.PI * (0.05 + 0.9 * k / n);
        pts.push([Math.cos(a) * top.r * 1.05, top.y - Math.sin(a) * 0.09, Math.sin(a) * top.r * 0.95 + 0.012]);
      }
      strip(s, pts, pts.map(() => 0.004), [0, 0, 1], () => rigid(sp2), { mat: it === 'rosary' ? MAT.wood : metal, region: REG.extra, hull: false, lw: 0.4 });
      const low = pts[Math.floor(n / 2)];
      if (it === 'cross') { box(s, add(low, [0, -0.03, 0.004]), [0.006, 0, 0], [0, 0.028, 0], [0, 0, 0.003], rigid(sp2), { mat: metal, region: REG.extra }); box(s, add(low, [0, -0.02, 0.005]), [0.016, 0, 0], [0, 0.006, 0], [0, 0, 0.003], rigid(sp2), { mat: metal, region: REG.extra }); }
      else if (it === 'bell') cone(s, add(low, [0, -0.005, 0.01]), [0, -1, 0.1], 0.018, 0.03, rigid(sp2), MAT.gold, 6);
      else box(s, add(low, [0, -0.015, 0.004]), [0.01, 0, 0], [0, 0.012, 0], [0, 0, 0.004], rigid(sp2), { mat: it === 'rosary' ? MAT.wood : MAT.gem, region: REG.extra });
    } else if (it === 'ruff') {
      loft(s, [{ c: [0, top.y + 0.004, 0.005], rs: top.r * 0.9, rf: top.r * 0.85, skin: rigid(sp2) }, { c: [0, top.y + 0.024, 0.005], rs: top.r * 1.55, rf: top.r * 1.5, skin: rigid(sp2), shape: (th) => 1 + 0.12 * Math.cos(th * TAU * 12) }],
        { sides: 16, front: [0, 0, 1], mat: MAT.parchment, region: REG.extra, tangent: () => [0, 1, 0] });
    } else if (it === 'pinceNez') {
      for (const sx of [1, -1]) {
        const g = ctx.rig.eyes[sx > 0 ? 0 : 1];
        const c = add(g.center as V3, [0, -0.004, g.dome + 0.012]);
        loft(s, [{ c, rs: g.rx * 1.05, rf: g.rx * 1.05, skin: rigid(head) }, { c: add(c, [0, 0, 0.003]), rs: g.rx * 1.05, rf: g.rx * 1.05, skin: rigid(head) }], { sides: 8, front: [0, 1, 0], mat: metal, region: REG.extra, tangent: () => [0, 0, 1] });
      }
    } else if (it === 'eyepatch') {
      const g = ctx.rig.eyes[0];
      const c = g.center as V3;
      const r = ctx.rig.skull.ring(Math.max(0, (c[1] - ctx.d.chin) / ctx.d.hH));
      const pts: V3[] = [];
      for (let k = 0; k <= 8; k++) { const a = Math.PI * (0.5 - 0.9 + k * (1.8 / 8)); pts.push([Math.cos(a) * r.rs * 1.04 * -1, c[1] + 0.012 + 0.03 * (k / 8), r.cz + Math.sin(a) * r.rf * 1.04]); }
      strip(s, pts, pts.map(() => 0.004), [0, 1, 0], () => rigid(head), { mat: MAT.leather, region: REG.extra, hull: false, lw: 0.3 });
    } else if (it === 'quill') {
      const e = ctx.P.ear_R_01!;
      strip(s, [add(e, [-0.01, -0.03, 0.02]), add(e, [-0.02, 0.06, -0.02])], [0.004, 0.012], [0, 0, 1], () => rigid(head), { mat: MAT.parchment, region: REG.extra });
    }
  });
}

function idleProp(ctx: BuildCtx, battle: boolean): void {
  const it = ctx.spec.o.idleProp;
  if (it === 'none' || battle) return;
  const so = ctx.rig.rig.sockets.prop_R!;
  const hand = so.bone;
  const piv = ctx.rig.rig.bones[hand].pivot;
  const at: V3 = scl([piv[0] + so.offset[0], piv[1] + so.offset[1], piv[2] + so.offset[2]], 1 / ctx.rig.norm);
  const sk = rigid(hand);
  part(ctx, 'garb.prop', (s) => {
    switch (it) {
      case 'book': box(s, add(at, [0, -0.01, 0.03]), [0.012, 0, 0], [0, 0.05, 0], [0, 0, 0.04], sk, { mat: MAT.dye1, region: REG.extra }); box(s, add(at, [0.0, -0.01, 0.03]), [0.013, 0, 0], [0, 0.046, 0], [0, 0, 0.036], sk, { mat: MAT.parchment, region: REG.extra }); break;
      case 'candle': box(s, add(at, [0, 0.02, 0.012]), [0.008, 0, 0], [0, 0.04, 0], [0, 0, 0.008], sk, { mat: MAT.parchment, region: REG.extra }); cone(s, add(at, [0, 0.062, 0.012]), [0, 1, 0], 0.006, 0.02, sk, MAT.gold, 4); break;
      case 'mug': loft(s, [{ c: add(at, [0, -0.03, 0.025]), rs: 0.028, rf: 0.028, skin: sk }, { c: add(at, [0, 0.04, 0.025]), rs: 0.03, rf: 0.03, skin: sk }], { sides: 7, front: [0, 0, 1], mat: MAT.wood, region: REG.extra, capStart: 'flat', capEnd: 'flat', tangent: () => [0, 1, 0] });
        fan(s, add(at, [0, 0.041, 0.025]), ellipse(add(at, [0, 0.041, 0.025]), [1, 0, 0], [0, 0, -1], 0.026, 0.026, 7), sk, { mat: MAT.parchment, region: REG.extra, hull: false }); break;
      case 'scroll': box(s, add(at, [0, 0, 0.02]), [0.008, 0, 0], [0, 0.008, 0], [0, 0, 0.05], sk, { mat: MAT.parchment, region: REG.extra }); break;
      case 'lily': strip(s, [add(at, [0, -0.02, 0.01]), add(at, [0, 0.12, 0.03])], [0.002, 0.002], [1, 0, 0], () => sk, { mat: MAT.tinctA, region: REG.extra }); cone(s, add(at, [0, 0.12, 0.03]), [0, 1, 0.2], 0.018, 0.035, sk, MAT.parchment, 5); break;
      case 'fish': {
        const c = add(at, [0, -0.05, 0.02]);
        const rs: Ring[] = [-1, -0.5, 0.2, 0.8].map((t) => ({ c: add(c, [0, t * 0.06, 0]), rs: 0.008 + 0.02 * Math.cos(t * 1.2), rf: 0.012 + 0.024 * Math.cos(t * 1.2), skin: sk }));
        loft(s, rs, { sides: 5, front: [0, 0, 1], mat: MAT.silver, region: REG.extra, capStart: 'point', capEnd: 'point', capStartLen: 0.01, capEndLen: 0.015, tangent: () => [0, 1, 0] });
        strip(s, [add(c, [0, -0.06, 0]), add(c, [0, -0.09, 0])], [0.004, 0.022], [0, 0, 1], () => sk, { mat: MAT.silver, region: REG.extra });
        break;
      }
      case 'lute': {
        const c = add(at, [0.03 * Math.sign(at[0]) * -1, 0.04, 0.06]);
        loft(s, [{ c: add(c, [0, 0, -0.02]), rs: 0.06, rf: 0.075, skin: sk }, { c: add(c, [0, 0, 0.015]), rs: 0.055, rf: 0.07, skin: sk }], { sides: 8, front: [0, 1, 0], mat: MAT.wood, region: REG.extra, capStart: 'point', capEnd: 'flat', capStartLen: 0.03, tangent: () => [0, 0, 1] });
        box(s, add(c, [0, 0.12, 0.012]), [0.012, 0, 0], [0, 0.08, 0], [0, 0, 0.006], sk, { mat: MAT.wood, region: REG.extra });
        break;
      }
      default: box(s, add(at, [0, 0, 0.02]), [0.015, 0, 0], [0, 0.03, 0], [0, 0, 0.015], sk, { mat: it === 'flask' ? MAT.gem : it === 'censer' ? MAT.gold : MAT.leather, region: REG.extra });
    }
  });
}

export function buildGarb(ctx: BuildCtx, opts: { battle: boolean }): void {
  garment(ctx);
  cape(ctx);
  headwear(ctx);
  wings(ctx);
  footwear(ctx);
  beltItems(ctx);
  neckItem(ctx);
  idleProp(ctx, opts.battle);
}

export { extrudeProfile };
